import React, { useEffect } from 'react';
import { Grid, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Btn, InputController, setProgress } from 'Components';
import Yup from 'Utils/Yup';
import { mapValidationErrors } from 'Utils/Http/MapValidationErrors';
import { CERTIFICATETHRESHOLD } from 'Constants/QueriesKeys';
import { CERTIFICATE_THRESHOLD_MAX, CERTIFICATE_THRESHOLD_MIN } from 'Constants/CertificateNames';
import { fetchCertificateThreshold, updateCertificateThreshold } from 'Services/CertificateNamesSvc';

const SAVE_THRESHOLD_BTN = 'SAVE_THRESHOLD_BTN';

// Named after the backend field, so a 422's errors.certificate_similarity_threshold
// lands on this input through mapValidationErrors without any renaming.
const THRESHOLD_FIELD = 'certificate_similarity_threshold';

// Every message is a bare string key, min() and max() included: Utils/Yup makes
// number min()/max() produce {key, values} objects, but
// Helpers/TranslateRHFErrorMsg - what InputController renders errors with -
// understands strings only, so an object would read "validation.[object Object]".
//
// typeError is the one an emptied input hits: yup casts '' to NaN, which fails
// the type check before required() is ever consulted.
const schema = Yup.object({
    [THRESHOLD_FIELD]: Yup.number()
        .typeError('certificateThresholdRange')
        .required('required')
        .min(CERTIFICATE_THRESHOLD_MIN, 'certificateThresholdRange')
        .max(CERTIFICATE_THRESHOLD_MAX, 'certificateThresholdRange'),
});

const useStyles = makeStyles((theme) => ({
    h4Text: {
        fontSize: '18px',
        color: 'rgb(3, 166, 90)',
        fontWeight: '500',
    },
}));

/**
 * Sets how similar a typed name has to be to an approved one before it counts
 * as verified - the backend's certificate_similarity_threshold, which the public
 * certificate page and the Verify Name tool are both judged by.
 *
 * The value in force is shown apart from the input, so an edit that has not
 * been saved never reads as the threshold the backend is applying.
 */
const ThresholdSettingCard = ({ onSaved }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const queryClient = useQueryClient();
    const spacing = 4;

    const thresholdQuery = useQuery([CERTIFICATETHRESHOLD], fetchCertificateThreshold, {
        refetchOnWindowFocus: false,
    });
    const threshold = thresholdQuery.data;
    // Until the GET lands the form holds useForm's empty default, not the stored
    // value. Saving from there would replace the setting without the operator
    // ever having seen it, so - as on the Season 4 page - Save stays locked until
    // a value has loaded. A refetch that fails later keeps the last good `data`,
    // so it does not lock a form that already has one.
    const loaded = threshold !== undefined;

    const form = useForm({
        resolver: yupResolver(schema),
        defaultValues: { [THRESHOLD_FIELD]: '' },
    });
    const { control, handleSubmit, reset, setError, formState } = form;
    // Read during render on purpose: RHF only keeps isDirty up to date for a form
    // that subscribes to it this way.
    const { isDirty } = formState;

    // Seed the input whenever the server's value changes - the first load, the
    // refetch on remount, a reconnect - but never over an edit in progress.
    useEffect(() => {
        if (threshold === undefined || isDirty) {
            return;
        }

        reset({ [THRESHOLD_FIELD]: threshold });
    }, [threshold, isDirty, reset]);

    const handleFormSubmit = async (values) => {
        setProgress(SAVE_THRESHOLD_BTN, true);
        try {
            // yupResolver hands over the cast values, so this is the number,
            // not the string the input holds.
            const saved = await updateCertificateThreshold(values[THRESHOLD_FIELD]);

            // Cache first: reset() re-renders straight away, and with the old
            // value still cached the effect above would briefly seed the input
            // with it.
            queryClient.setQueryData([CERTIFICATETHRESHOLD], saved);
            reset({ [THRESHOLD_FIELD]: saved });

            toast.success(t('admin.certificateNames.threshold.saved', { threshold: saved }));
            onSaved?.(saved);
        } catch (error) {
            const fieldErrors = error?.response?.data?.errors;

            // Only a 422 about the threshold itself has a field to land on. One
            // about anything else - an older backend that still requires
            // season_4_enabled, say - would map onto nothing and vanish, so it
            // gets the toast instead.
            if (error?.response?.status === 422 && fieldErrors?.[THRESHOLD_FIELD]) {
                mapValidationErrors(fieldErrors).forEach(({ name, message, type }) => {
                    setError(name, { type, message });
                });
            } else {
                toast.error(
                    error?.response?.data?.message ||
                        t('admin.certificateNames.threshold.saveFailed'),
                );
            }
        }
        setProgress(SAVE_THRESHOLD_BTN, false);
    };

    return (
        <Paper variant="outlined" className="mb-8 p-4">
            <h4 className={classes.h4Text}>{t('admin.certificateNames.threshold.title')}</h4>
            <p className="text-xs text-gray-500 pt-1 pb-3">
                {t('admin.certificateNames.threshold.intro')}
            </p>

            {loaded ? (
                <p className="text-base font-medium pb-4">
                    {t('admin.certificateNames.threshold.current', { threshold })}
                </p>
            ) : (
                <p className="text-xs text-gray-500 pb-4">
                    {thresholdQuery.isError
                        ? t('admin.certificateNames.threshold.loadFailed')
                        : t('admin.certificateNames.threshold.loading')}
                </p>
            )}

            {/* noValidate: min and max are on the input for its spinner. Left to
                the browser they would also block the submit with a native
                bubble in the browser's own language, and the messages under the
                field would never show. */}
            <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
                <Grid container spacing={spacing} alignItems="flex-end">
                    <Grid item xs={12} md={6}>
                        <InputController
                            control={control}
                            name={THRESHOLD_FIELD}
                            type="number"
                            label={t('admin.certificateNames.threshold.label')}
                            // Nothing to edit until there is a value to edit.
                            disabled={!loaded}
                            inputProps={{
                                min: CERTIFICATE_THRESHOLD_MIN,
                                max: CERTIFICATE_THRESHOLD_MAX,
                                // A fraction such as 85.5 is a valid threshold,
                                // not a step mismatch.
                                step: 'any',
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Btn
                            fullWidth={false}
                            type="submit"
                            name={SAVE_THRESHOLD_BTN}
                            // Only ever pass disabled when it should be true:
                            // BtnBase spreads props after its own
                            // disabled={isLoading}, so a literal false would
                            // re-enable the button mid-save.
                            {...(loaded ? {} : { disabled: true })}
                        >
                            {t('admin.certificateNames.threshold.save')}
                        </Btn>
                    </Grid>
                </Grid>
            </form>

            <p className="text-xs text-gray-500 pt-3">
                {t('admin.certificateNames.threshold.rangeHint')}
            </p>
        </Paper>
    );
};

export default ThresholdSettingCard;
